import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Loader2, Mail, Lock, User } from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/8089172/pexels-photo-8089172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/dashboard";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "owner" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate(nextPath);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-[calc(100vh-73px)] lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2C3830]/40" />
      </div>
      <div className="flex items-center justify-center px-5 py-12 lg:px-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#2C3830] text-white"><Building2 className="h-5 w-5" /></span>
            <span className="font-display text-2xl font-semibold text-[#2C3830]">RENTO</span>
          </div>
          <h1 className="mt-8 font-display text-3xl font-medium tracking-tight text-[#1A1A1A]">Create your account</h1>
          <p className="mt-2 text-[#6B7280]">Start listing properties in minutes.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Full name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                <Input data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" required className="rounded-xl pl-9" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">I am a</Label>
              <div className="flex gap-3">
                {[{ v: "owner", l: "Property Owner" }, { v: "agent", l: "Listing Agent" }].map((r) => (
                  <button
                    key={r.v}
                    type="button"
                    data-testid={`role-${r.v}`}
                    onClick={() => set("role", r.v)}
                    className={`flex-1 rounded-xl border p-3 text-sm font-medium transition-colors ${
                      form.role === r.v ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border text-[#2C3830] hover:border-[#2C3830]"
                    }`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" required className="rounded-xl pl-9" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 6 characters" required minLength={6} className="rounded-xl pl-9" />
              </div>
            </div>
            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#2C3830] py-6 text-white hover:bg-[#3A4A40]">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-[#6B7280]">
            <span className="h-px flex-1 bg-border" /> OR <span className="h-px flex-1 bg-border" />
          </div>
          <Button data-testid="register-google" onClick={google} variant="outline" className="w-full rounded-full py-6">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="mr-2 h-5 w-5" />
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-sm text-[#6B7280]">
            Already have an account? <Link to="/login" data-testid="go-login" className="font-medium text-[#2C3830] underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
